export class ConversationService {
    #conversations;
    #id;
    #notifications;
    #now;
    #outbox;
    constructor(dependencies, options) {
        this.#conversations = dependencies.conversations;
        this.#id = options.id;
        this.#notifications = dependencies.notifications;
        this.#now = options.now ?? (() => new Date().toISOString());
        this.#outbox = dependencies.outbox;
    }
    async sendMessage(input) {
        requireText(input.body, "message body");
        requireText(input.conversationId, "conversation id");
        requireText(input.idempotencyKey, "message idempotency key");
        requireText(input.senderId, "message sender id");
        const createdAt = this.#now();
        const message = {
            body: input.body,
            ...(input.messageId === undefined ? {} : { clientMessageId: input.messageId }),
            conversationId: input.conversationId,
            createdAt,
            id: input.messageId ?? this.#id(),
            senderId: input.senderId,
            state: "pending",
        };
        await this.#conversations.appendPendingMessage({ message }).commit();
        const outbox = {
            createdAt,
            domain: "conversation",
            id: `conversation:${message.id}`,
            idempotencyKey: input.idempotencyKey,
            payload: {
                body: message.body,
                conversationId: message.conversationId,
                messageId: message.id,
                operation: "conversation.send-message",
                senderId: message.senderId,
            },
            state: "pending",
            updatedAt: createdAt,
        };
        await this.#outbox.create(outbox);
        return message;
    }
    async applyDelta(delta) {
        switch (delta.kind) {
            case "notification.read":
            case "notification.upsert":
                await this.#notifications.applyDelta(delta).commit();
                return;
            case "conversation.upsert":
            case "message.ack":
            case "message.append":
            case "message.fail":
                await this.#conversations.applyDelta(delta).commit();
                return;
        }
    }
    async acknowledgeMessage(input) {
        await this.#conversations.acknowledgeMessage({
            acknowledgedAt: input.acknowledgedAt ?? this.#now(),
            messageId: input.messageId,
            ...(input.serverMessageId === undefined ? {} : { serverMessageId: input.serverMessageId }),
        });
    }
    async failMessage(input) {
        requireText(input.reason, "message failure reason");
        await this.#conversations.failMessage({
            failedAt: input.failedAt ?? this.#now(),
            messageId: input.messageId,
            reason: input.reason,
        });
    }
    async listConversations(params) {
        return await this.#conversations.listConversations(params);
    }
    async listMessages(conversationId, params) {
        return await this.#conversations.listMessages(conversationId, params);
    }
    async listNotifications(params) {
        return await this.#notifications.listNotifications(params);
    }
    async markNotificationRead(notificationId, readAt = this.#now()) {
        requireText(notificationId, "notification id");
        await this.#notifications.markRead({ notificationId, readAt }).commit();
    }
}
function requireText(value, label) {
    if (value.trim() === "")
        throw new TypeError(`${label} must be a non-empty string`);
}
//# sourceMappingURL=conversation-service.js.map